import { createAxiosInstance } from "./index";
import hostConfig from "@/config/hostResolver";

const baseUrl = process.env.VUE_APP_USER_V3_URL ?? `${hostConfig.apiBaseUrl}/user-service-ts`;

class UserServiceV3 {
  get apiClient() {
    if (!this._apiClient) this._apiClient = createAxiosInstance(baseUrl);
    return this._apiClient;
  }

  /**
   * Public pre-login lookup of the sign-in methods for an email or phone number.
   * Sending `domain` opts into the whitelabel host check; a denial comes back as
   * `{ hostDenied: true, redirectDomain }` in place of the options.
   */
  async getLoginOptions(identifier, domain) {
    const body = { identifier };
    if (domain) body.domain = domain;
    const result = (
      await this.apiClient?.post("/v3/user/login/options", body, {
        errorContext: "Failed to get login options",
        silent: true,
        skipAuth: true,
      })
    )?.data?.result;
    if (!result) throw new Error("Login options response had no result");
    if (result.state?.hostDenied)
      return { hostDenied: true, redirectDomain: result.state.redirectDomain || null };
    return result;
  }

  async getMergePreview(userId) {
    return (
      await this.apiClient?.get(`/v3/user/merge/preview/${userId}`, {
        errorContext: "Failed to get merge preview",
      })
    )?.data;
  }

  async mergeUsers(removeUserId, keepUserId) {
    return (
      await this.apiClient?.post(
        "/v3/user/merge",
        {
          loserId: removeUserId,
          winnerId: keepUserId,
        },
        { errorContext: "Failed to merge users", retryRequest: false }
      )
    )?.data;
  }

  async saveTaxInfo(data, { issueCard = false } = {}) {
    return (
      await this.apiClient?.post("/v3/user/tax-info", data, {
        params: issueCard ? { issueCard: true } : undefined,
        errorContext: "Failed to save tax info",
        silent: true,
      })
    )?.data;
  }

  async updateTaxInfo(data) {
    return (
      await this.apiClient?.patch("/v3/user/tax-info", data, {
        errorContext: "Failed to update tax info",
        silent: true,
      })
    )?.data;
  }

  async clearTaxInfo(data) {
    return (
      await this.apiClient?.delete("/v3/user/tax-info", {
        data,
        errorContext: "Failed to clear previous tax info",
        silent: true,
      })
    )?.data;
  }

  async getTaxInfoStatus() {
    return (
      await this.apiClient?.get("/v3/user/tax-info/status", {
        errorContext: "Failed to get tax info status",
      })
    )?.data;
  }

  // Admin view of another user; reports only whether a number is on file, never the number.
  // `clientId` is only honoured for internal admins - everyone else is pinned to their own.
  async getTaxInfoStatusForUser(userId, clientId) {
    return (
      await this.apiClient?.get(`/v3/user/tax-info/status/${encodeURIComponent(userId)}`, {
        params: { clientId },
        errorContext: "Failed to get tax info status",
        silent: true,
      })
    )?.data;
  }

  // Tax config credentials below are vaulted in GCP, so the status endpoints only ever
  // report whether they resolve plus the plaintext tenant id - never the secrets.
  // `clientId` is only honoured for internal admins - everyone else is pinned to their own.
  async getWirConfigStatus(clientId) {
    return (
      await this.apiClient?.get("/v3/wir-config/status", {
        params: { clientId },
        errorContext: "Failed to get WIR config status",
        silent: true,
      })
    )?.data;
  }

  // POST creates the full bundle; PATCH updates whichever fields are supplied and 404s
  // when no config exists yet.
  async createWirConfig(data, clientId) {
    return (
      await this.apiClient?.post("/v3/wir-config", data, {
        params: { clientId },
        errorContext: "Failed to save WIR credentials",
        silent: true,
      })
    )?.data;
  }

  async updateWirConfig(data, clientId) {
    return (
      await this.apiClient?.patch("/v3/wir-config", data, {
        params: { clientId },
        errorContext: "Failed to update WIR credentials",
        silent: true,
      })
    )?.data;
  }

  async getDataVaultConfigStatus(clientId) {
    return (
      await this.apiClient?.get("/v3/data-vault-config/status", {
        params: { clientId },
        errorContext: "Failed to get Data Vault config status",
        silent: true,
      })
    )?.data;
  }

  async createDataVaultConfig(data, clientId) {
    return (
      await this.apiClient?.post("/v3/data-vault-config", data, {
        params: { clientId },
        errorContext: "Failed to save Data Vault credentials",
        silent: true,
      })
    )?.data;
  }

  async updateDataVaultConfig(data, clientId) {
    return (
      await this.apiClient?.patch("/v3/data-vault-config", data, {
        params: { clientId },
        errorContext: "Failed to update Data Vault credentials",
        silent: true,
      })
    )?.data;
  }

  // Validates WIR + Data Vault connectivity. Omitted fields fall back to the saved
  // config, and nothing sent here is persisted.
  async testTaxConfigConnection(data, clientId) {
    return (
      await this.apiClient?.post("/v3/data-vault-config/test-connection", data, {
        params: { clientId },
        errorContext: "Failed to test the tax config connection",
        silent: true,
      })
    )?.data;
  }
}

export default new UserServiceV3();
